import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {Container, Graphics} from 'pixi.js';
import {inject} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {ILogic} from '@gongxigames/framework/slot/types';
import {ReelConstants} from '@gongxigames/framework/slot/utils';
import { IndependentReelsView } from '@gongxigames/framework/slot/modules/reels/IndependentReelsView';
import { FirecatcherReelView } from '@gongxigames/framework/slot/modules/reels/FirecatcherReelView';
import { ReelView } from '@gongxigames/framework/slot/modules/reels/ReelView';
import { ReelViewData } from '@gongxigames/framework/slot/modules/reels/ReelView';

export abstract class FirecatcherReelsView extends IndependentReelsView {
    public static readonly NAME: symbol = Symbol('FirecatcherReelsView');

    private static readonly FADER_ALPHA: number = 0.7;

    @inject(DI.Logic)
    protected logic!: ILogic;

    protected _faderLayer!: Container;
    protected _jpSymbolsContainer!: Container;
    protected _gridLayer!: Container;
    protected _reels: FirecatcherReelView[] = [];

    public startSpinning(): void {
        this._isQuickStop = false;
        this.setupReelSetMaskForSpinning();
        this.removeOversizedsFromSpecialLayer();

        this.logic.getFirecatcherPrizes().forEach((prize, index) => {
            if (prize === 0) {
                this._reels[index].startSpinning();
            }
        });
    }

    public setupReelStrip(reels: Reel[], reelConstants?: typeof ReelConstants): void {
        super.setupReelStrip(reels, reelConstants);

        this.restorePrevReelSet();
    }

    public setupReelStripWithPositions(reels: Reel[], positions: number[]): void {
        super.setupReelStripWithPositions(reels, positions);

        this.restorePrevReelSet();
    }

    protected restorePrevReelSet(): void {
        this.restoreSymbols();
        this.restoreJPsymbols();
    }

    protected restoreJPsymbols(): void {
        this.logic.getFirecatcherPrizes().forEach((value, index) => {
            if (value !== 0) {
                this.replaceSymbol({x: index, y: 0}, this.getJPSymbol());
                this.putOversizedsOnSpecialLayer(index);
            }
        });
    }

    protected createReelSet(reels: Reel[], reelsPosition: number[]): void {
        this._symbolsPool.freeAll();
        this.setupReelsLayers();

        for (let reelIndex = 0; reelIndex < reels.length; reelIndex++) {
            const reelData = this.getReelData(reelIndex);

            if (reelsPosition && reelsPosition[reelIndex]) {
                reelData.startPosition = reelsPosition[reelIndex];
            }

            const strip = reels[reelIndex].getSymbols().map(symbol => {
                return symbol === this.getBlankSymbol() ? this.getSymbolInsteadBlank() : symbol;
            });

            const reel = this.createJPReel(reelData, this._jpSymbolsContainer);

            reel.strip = strip;
            reel.init(this.objectBuilder, this.spineUtils);

            reel.onReelStopping = this.onReelStopping.bind(this);
            reel.onReelStopped = this.onReelStopped.bind(this);
            reel.onReelAnimationFinished = this.onReelAnimationFinished.bind(this);

            this.updateManager.add(reel);
            reel.start();
            this._reels.push(reel);
        }

        this.setReelsBackground();
        this.forceUpdateOrientation();
    }

    protected setupReelsLayers(): void {
        for (const reel of this._reels) {
            this.updateManager.remove(reel);
        }

        this._reels = [];
        this.container.removeChildren();

        this._reelsetContainer = new Container();
        this._jpSymbolsContainer = new Container();
        this._oversizedsOnStoppedReelsLayer = new Container();
        this._reelSetAnimationContainer = new Container();
        this.initFaderLayer();
        this.initGridLayer();
        this.container.addChild(this._reelsetContainer);
        this.container.addChild(this._faderLayer);
        this.container.addChild(this._jpSymbolsContainer);
        this.container.addChild(this._gridLayer);
        this.container.addChild(this._oversizedsOnStoppedReelsLayer);
        this.container.addChild(this._reelSetAnimationContainer);
    }

    protected initGridLayer(): void {
        this._gridLayer = new Container();
    }

    protected initFaderLayer(): void {
        this._faderLayer = new Container();
        this._faderLayer.addChild(this.createFader());
    }

    protected createFader(): Graphics {
        const reels = this.reelConstants.REELS;
        const symbol = this._reelConstants.SYMBOL_SLOT;
        const faderWidth = reels[reels.length - 1].x + symbol.width - reels[0].x;
        const faderHeight = reels[reels.length - 1].y + symbol.height - reels[0].y;
        const fader = this._displayObjectUtils.createFader(FirecatcherReelsView.FADER_ALPHA, faderWidth, faderHeight);

        fader.x = reels[0].x;
        fader.y = reels[0].y;

        return fader;
    }

    public setReelsBackground(): void {
        for (const reel of this._reels as FirecatcherReelView[]) {
            reel.setRespinGameBackground();
        }
    }

    protected createReel(): ReelView {
        return null!;
    }

    protected createJPReel(reelData: ReelViewData, jpSymbolsContainer: Container): FirecatcherReelView {
        return new FirecatcherReelView(reelData, jpSymbolsContainer, this.getJPSymbol());
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(
            this._faderLayer,
            this._gridLayer,
            this._jpSymbolsContainer
        );
    }

    protected abstract restoreSymbols(): void;

    public abstract getJPSymbol(): number;
}
